import React, { useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import ScheduleAPI from '../../../../../service/schedule/ScheduleAPI';
import useWatchError from '../../../../../shared/hooks/useWatchError';
import Table from '../../../../../theme/ui/Atoms/Table/Table';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux/store.model';
import { getFeatureToggle } from '../../../../../shared/featureToggle';
import { DevicesStoreInfoResponse } from '../../../../../service/store/StoreService.model';
import { getHighestParentGroupFromGroupIdSelector } from '../../../../../redux/structure/selectors/Group.selector';
import { DeviceUnit } from './DeviceUnit';
import { useParams } from 'react-router-dom';
import { getDuplicatedDevicesInList } from '../../../../../service/device/util/getDeviceMappings';
import { useGetGroupByIdQuery } from '../../../../../service/group/GroupAPI';
import { CommanderAPI } from '../../../../../service/device/CommanderAPI/CommanderAPI';
import { waitPromise } from '../../../../../shared/util/waitPromise';

interface ManagerViewRegionProps {
  devices?: DevicesStoreInfoResponse[];
}
export default function ManagerViewStore(props: ManagerViewRegionProps) {
  const { devices } = props;
  const dispatch = useAppDispatch();
  const { groupId: storeId, marketId } = useParams<{
    groupId: string;
    marketId: string;
  }>();
  const { data } = useGetGroupByIdQuery(storeId ?? '');
  const composableDevices = data?.childGroups;
  const visibleDevices = devices?.filter((device) => device.visible);

  const [syncAirConnectStatusByGroup] =
    CommanderAPI.endpoints.syncAirConnectStatusByGroup.useMutation();

  useEffect(() => {
    if (!storeId) return;

    syncAirConnectStatusByGroup(storeId)
      .unwrap()
      .then(
        () => waitPromise(6000) // wait for 6 seconds to ensure the status is updated before fetching
      )
      .then(() =>
        dispatch(
          CommanderAPI.util.invalidateTags([
            { type: 'group-actions', id: storeId },
          ])
        )
      )
      .catch((error) => {
        console.error(
          'Error syncing AirConnect status for group: ',
          storeId,
          error
        );
      });
  }, [dispatch, storeId, syncAirConnectStatusByGroup]);
  const { t } = useTranslation();

  const [deviceEditing, setDeviceEditing] = useState<string | null>(null);

  const higherGroupId =
    useAppSelector((state) =>
      getHighestParentGroupFromGroupIdSelector(state, {
        workspaceId: marketId,
        groupId: storeId ?? '',
      })
    ) ?? '';
  const {
    error: errorSchedules,
    isLoading: isLoadingSchedules,
    isFetching: isFetchingSchedules,
    data: schedules,
  } = ScheduleAPI.useGetSchedulesByGroupQuery(higherGroupId ?? '');

  const optionsSchedule = useMemo(
    () =>
      schedules?.map((schedule) => ({
        text: schedule.scheduleName,
        value: schedule.scheduleId,
      })) ?? [],
    [schedules]
  );

  useWatchError(errorSchedules);

  const duplicatedDevice = useMemo(
    () =>
      getDuplicatedDevicesInList(visibleDevices ?? [], composableDevices ?? []),
    [visibleDevices, composableDevices]
  );

  const hasAnyDeviceWithMac = useMemo(
    () => visibleDevices?.some((device) => device.mac),
    [visibleDevices]
  );

  const { Schedule } = useAppSelector(getFeatureToggle);

  return (
    <Table
      sx={{
        '& th.header_store': {
          maxWidth: 200,
        },
      }}
    >
      <thead style={{ border: 'none' }}>
        <tr>
          <Typography
            component={'th'}
            className="header_store"
            color="primary"
            variant="subtitle2"
            sx={{
              textAlign: 'left!important',
            }}
          >
            {t('view_locations.header_device_name')}
          </Typography>
          <Typography component={'th'} color="primary" variant="subtitle2">
            {t('view_locations.header_model')}
          </Typography>
          {!!Schedule && (
            <Typography component={'th'} color="primary" variant="subtitle2">
              {t('schedule.settings.view_locations.schedule_header')}
            </Typography>
          )}

          <Typography component={'th'} color="primary" variant="subtitle2">
            {t('view_locations.header_status')}
          </Typography>
          <Typography component={'th'} color="primary" variant="subtitle2">
            {t('view_locations.header_alarms')}
          </Typography>
          {hasAnyDeviceWithMac && (
            <Typography component={'th'} color="primary" variant="subtitle2">
              {t('view_locations.header_rssi')}
            </Typography>
          )}
          <Typography component={'th'} color="primary" variant="subtitle2">
            {t('view_locations.header_actions')}
          </Typography>
        </tr>
      </thead>
      <tbody>
        {visibleDevices?.map((device) => (
          <DeviceUnit
            key={device.uuid}
            device={device}
            deviceEditing={deviceEditing}
            duplicated={duplicatedDevice?.find(
              (duplicated) => duplicated.id === device.uuid
            )}
            isLastComposableDevice={
              visibleDevices?.filter(
                (d) =>
                  d.commanderId === device.commanderId &&
                  d.composableDeviceId !== null
              ).length === 1
            }
            setDeviceEditing={setDeviceEditing}
            schedules={{
              displaySchedule: Schedule,
              optionsSchedule,
              isLoadingSchedules,
              isFetchingSchedules,
            }}
            showRSSIColumn={hasAnyDeviceWithMac}
          />
        ))}
      </tbody>
    </Table>
  );
}
